import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Share2, RotateCcw, GitCompareArrows, X } from "lucide-react";
import { PaddleCard } from "@/components/PaddleCard";
import { MiniCart } from "@/components/MiniCart";
import { toast } from "sonner";
import { saveQuiz } from "@/lib/api";

export default function Results({ results, answers, resetQuiz, cart, addToCart, removeFromCart }) {
  const navigate = useNavigate();
  const [compareActive, setCompareActive] = useState(false);
  const [compareIds, setCompareIds] = useState([]);
  const [cartOpen, setCartOpen] = useState(false);
  const [savedId, setSavedId] = useState(null);
  const [saving, setSaving] = useState(false);

  const compareSelected = useMemo(() => new Set(compareIds), [compareIds]);

  const toggleCompare = (p) => {
    setCompareIds((ids) => {
      if (ids.includes(p.product_id)) return ids.filter((i) => i !== p.product_id);
      if (ids.length >= 2) {
        toast("You can compare up to 2 paddles");
        return ids;
      }
      return [...ids, p.product_id];
    });
  };

  const handleAdd = (p, variant) => {
    if (!variant) return;
    addToCart({
      product_id: p.product_id,
      variant_id: variant.variant_id,
      variant_title: variant.title,
      title: p.title,
      price: variant.price || p.price,
      image: p.image,
    });
    toast.success(`${p.title} added to cart`);
    setCartOpen(true);
  };

  const goCompare = () => {
    if (compareIds.length < 2) {
      toast("Pick 2 paddles to compare");
      return;
    }
    const selected = results.filter((r) => compareIds.includes(r.product_id));
    navigate("/compare", { state: { paddles: selected } });
  };

  const handleShare = async () => {
    setSaving(true);
    try {
      const saved = await saveQuiz(answers, results);
      setSavedId(saved.id);
      const url = `${window.location.origin}/share/${saved.id}`;
      try {
        await navigator.clipboard.writeText(url);
        toast.success("Share link copied to clipboard!", { description: url });
      } catch {
        toast.success("Link ready", { description: url });
      }
    } catch (e) {
      toast.error("Couldn't save your results");
    } finally {
      setSaving(false);
    }
  };

  if (!results || results.length === 0) {
    return (
      <div className="cd-container" data-testid="results-empty">
        <div className="cd-card" style={{ padding: 24, textAlign: "center" }}>
          <h2 className="cd-h2">No recommendations yet</h2>
          <p className="cd-sub" style={{ marginTop: 8 }}>Take the quiz to see paddles picked for you.</p>
          <button className="cd-btn-primary" style={{ marginTop: 16 }} onClick={() => navigate("/quiz")}>
            Start quiz
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="cd-container-wide cd-court-bg cd-fade-in" data-testid="results-page">
      <div style={{ display: "flex", flexWrap: "wrap", gap: 12, alignItems: "flex-end", justifyContent: "space-between", marginBottom: 20 }}>
        <div>
          <span className="cd-chip cd-chip-primary" style={{ marginBottom: 10 }}>
            Your matches
          </span>
          <h1 className="cd-h1" style={{ marginTop: 8 }}>
            {results.length === 1 ? "Your paddle." : `Your top ${results.length} paddles.`}
          </h1>
          <p className="cd-sub" style={{ marginTop: 6 }}>
            Ranked by match score. Add to cart, compare, or save the list.
          </p>
        </div>

        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <button
            className={`cd-btn-secondary ${compareActive ? "" : ""}`}
            onClick={() => { setCompareActive(!compareActive); if (compareActive) setCompareIds([]); }}
            data-testid="toggle-compare-btn"
          >
            <GitCompareArrows size={16} />
            {compareActive ? "Exit compare" : "Compare"}
          </button>
          <button
            className="cd-btn-secondary"
            onClick={handleShare}
            disabled={saving}
            data-testid="share-btn"
          >
            <Share2 size={16} /> {saving ? "Saving…" : "Share"}
          </button>
          <button className="cd-btn-secondary" onClick={resetQuiz} data-testid="retake-btn">
            <RotateCcw size={16} /> Retake
          </button>
        </div>
      </div>

      <div className="cd-results-grid">
        {results.map((p, idx) => (
          <PaddleCard
            key={p.product_id}
            paddle={p}
            isTop={idx === 0}
            compareActive={compareActive}
            compareSelected={compareSelected.has(p.product_id)}
            onToggleCompare={toggleCompare}
            onAdd={handleAdd}
          />
        ))}
      </div>

      {savedId && (
        <div className="cd-card" style={{ padding: 16, marginTop: 20, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" }} data-testid="share-link-card">
          <div>
            <div style={{ fontWeight: 600, fontSize: 14 }}>Shareable link ready</div>
            <div className="cd-micro" style={{ wordBreak: "break-all" }}>{window.location.origin}/share/{savedId}</div>
          </div>
          <button
            className="cd-btn-ghost"
            onClick={() => setSavedId(null)}
            aria-label="Dismiss"
          >
            <X size={16} />
          </button>
        </div>
      )}

      <div style={{ position: "fixed", bottom: 20, right: 20, zIndex: 40 }}>
        <button
          className="cd-btn-primary"
          onClick={() => setCartOpen(true)}
          data-testid="open-cart-btn"
          style={{ borderRadius: 999, padding: "12px 18px" }}
        >
          Cart ({cart.length})
        </button>
      </div>

      {compareActive && compareIds.length > 0 && (
        <div className="cd-compare-bar" data-testid="compare-bar">
          <span>{compareIds.length}/2 selected</span>
          <button className="cd-compare-cta" onClick={goCompare} data-testid="compare-go-btn">
            Compare
          </button>
        </div>
      )}

      <MiniCart open={cartOpen} onOpenChange={setCartOpen} items={cart} onRemove={removeFromCart} />
    </div>
  );
}
